import dayjs from 'dayjs';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useNavigate } from 'react-router-dom';

import {
    MarkAllNotificationsAsReadDocument,
    MarkNotificationsAsReadDocument,
    NotificationListDocument,
    NotificationTypes,
    UnreadNotificationCountDocument,
    UpdateSingleNotificationFragmentDoc,
} from '@graphql/generated/graphql';
import { updateUnreadNotificationCount } from '@graphql/helpers';

import { getPath } from '@services/RouteService/paths';

import { gql, useMutation, useQuery } from '@apollo/client';

const DOWNLOAD_LIST_ITEMS_NUMBER = 20;
const INFINITE_SCROLL_OFFSET_BOTTOM = 200;

interface NotificationsListOptions {
    onClose: () => void;
}

export const useNotificationsList = ({ onClose }: NotificationsListOptions) => {
    const navigate = useNavigate();

    const [markAllAsRead] = useMutation(MarkAllNotificationsAsReadDocument, {
        context: {
            notifyOnError: true,
        },
        onError: () => {},
        optimisticResponse: {
            notifications: [],
        },
        refetchQueries: [
            {
                query: UnreadNotificationCountDocument,
            },
        ],
        update: (cache, { data }) => {
            data && updateUnreadNotificationCount('CLEAR');
            const notificationList = cache.readQuery({
                query: NotificationListDocument,
            });

            notificationList?.listNotifications.page.edges?.map(
                (singleNotification) => {
                    if (singleNotification.node.readNotification?.length)
                        return;

                    cache.writeFragment({
                        id: `Notification:${singleNotification.node.uuid}`,
                        data: {
                            readNotification: [
                                {
                                    readAt: dayjs().toISOString(),
                                },
                            ],
                        },
                        fragment: gql`
                            fragment UpdateSingleNotification on Notification {
                                readNotification {
                                    readAt
                                }
                            }
                        `,
                    });
                },
            );
        },
    });

    const [markAsRead] = useMutation(MarkNotificationsAsReadDocument, {
        context: {
            notifyOnError: true,
        },
        onError: () => {},
        optimisticResponse: (variables) => ({
            notifications:
                typeof variables.notificationsUuids === 'string'
                    ? [variables.notificationsUuids]
                    : variables.notificationsUuids,
        }),
        update: (cache, { data }) => {
            if (!data) return;
            const notificationsLength = data.notifications.length;

            notificationsLength &&
                updateUnreadNotificationCount(notificationsLength);

            data.notifications.forEach((notification) =>
                cache.writeFragment({
                    id: `Notification:${notification}`,
                    data: {
                        readNotification: [
                            {
                                readAt: dayjs().toDate(),
                            },
                        ],
                    },
                    fragment: UpdateSingleNotificationFragmentDoc,
                }),
            );
        },
    });

    const { data, error, fetchMore, loading } = useQuery(
        NotificationListDocument,
        {
            fetchPolicy: 'network-only',
            nextFetchPolicy: 'cache-first',
            variables: {
                paginationInput: {
                    offset: 1,
                    size: DOWNLOAD_LIST_ITEMS_NUMBER,
                },
            },
        },
    );

    const notificationsData = data?.listNotifications.page;
    const notifications = notificationsData?.edges;
    const notificationsLength = notifications?.length ?? 0;
    const hasNextPage = !!notificationsData?.info.hasNextPage;

    const [infiniteRef, { rootRef }] = useInfiniteScroll({
        disabled: !!error,
        hasNextPage,
        loading,
        onLoadMore: () =>
            void fetchMore?.({
                variables: {
                    paginationInput: {
                        offset: notificationsLength + 1,
                        size: DOWNLOAD_LIST_ITEMS_NUMBER,
                    },
                },
            }),
        rootMargin: `0px 0px ${INFINITE_SCROLL_OFFSET_BOTTOM}px 0px`,
    });

    const notificationNavigate = (
        disclosureUuid?: string | null,
        notificationType?: NotificationTypes | null,
        refUuid?: string | null,
    ) => {
        if (
            notificationType === 'NEW_SURVEY' ||
            notificationType === 'SURVEY_REMINDER'
        ) {
            navigate(
                getPath({
                    params: { surveyId: refUuid ?? '' },
                    path: '/survey/details/:surveyId',
                }),
            );
            return;
        }

        if (notificationType === 'DISCLOSURE_CHAT') {
            navigate(
                getPath({
                    params: {
                        disclosureUuid: disclosureUuid ?? '',
                        submissionUuid: refUuid ?? '',
                    },
                    path: '/disclosure/list/:disclosureUuid/:submissionUuid',
                }),
            );
            return;
        }

        if (
            notificationType === 'DISCLOSURE_RESPONSE_APPROVAL_APPROVED' ||
            notificationType === 'DISCLOSURE_RESPONSE_APPROVAL_DISAPPROVED'
        ) {
            navigate(
                getPath({
                    params: {
                        disclosureUuid: disclosureUuid ?? '',
                        submissionUuid: refUuid ?? '',
                    },
                    path: '/disclosure/list/:disclosureUuid/:submissionUuid',
                }),
            );
            return;
        }

        if (refUuid) {
            navigate(
                getPath({
                    params: {
                        caseId: refUuid,
                    },
                    path: '/incidents/reports/:caseId',
                }),
            );
            return;
        }

        navigate(getPath({ path: '/incidents/reports' }));
    };

    const handleNotificationOpen = (
        id: string,
        disclosureUuid?: string | null,
        refUuid?: string | null,
        type?: NotificationTypes | null,
        isRead?: boolean,
    ) => {
        !isRead &&
            void markAsRead({
                variables: {
                    notificationsUuids: [id],
                },
            });

        onClose();
        notificationNavigate(disclosureUuid, type, refUuid);
    };

    return {
        handleNotificationOpen,
        hasNextPage,
        infiniteRef,
        loading,
        markAllAsRead,
        notifications,
        notificationsLength,
        rootRef,
    };
};
